import { z } from 'zod';

/**
 * `pending-gitlab-jenkins-depth` — provider-aware CI integration depth.
 *
 * Shared between the CLI (`packages/cli/src/commands/run.ts` +
 * `packages/cli/src/ci/`) and any future server-side renderer that
 * wants to label a run with its originating CI provider.
 *
 * The schemas here are deliberately conservative: they describe the
 * normalised shape the CLI works against, not the full surface of
 * every host's REST API. The host-specific request bodies live in
 * the emitter / poster modules under `packages/cli/src/ci/` so
 * `@rynora/contracts` stays free of host-specific churn.
 *
 * Design choices:
 *   • **Detection is pure.** `detectCiProvider(env)` takes an env
 *     bag (NOT `process.env` directly) so unit tests can pass a
 *     stub map and CI harnesses can pre-redact secrets before
 *     calling.
 *   • **`auto` is a CLI-flag value only.** It never appears inside
 *     a `CiEnv` — by the time we've built the env shape, the
 *     `provider` field is one of the four concrete values
 *     (`github` / `gitlab` / `jenkins` / `unknown`).
 *   • **Order matters.** When two provider fingerprints are
 *     present (extremely rare — nested CI), the outermost wins.
 *     The constant `CI_DETECT_ORDER` documents this so the
 *     priority can't drift between detection and downstream
 *     rendering.
 */

export const CiProviderSchema = z.enum(['auto', 'github', 'gitlab', 'jenkins']);
export type CiProvider = z.infer<typeof CiProviderSchema>;

/**
 * The concrete-provider subset of `CiProvider`. `auto` is the CLI
 * sentinel; once detection runs the resulting env is one of these.
 */
export const CiConcreteProviderSchema = z.enum(['github', 'gitlab', 'jenkins', 'unknown']);
export type CiConcreteProvider = z.infer<typeof CiConcreteProviderSchema>;

/**
 * Normalised env shape across the three providers. Every field is
 * nullable because no host guarantees every signal — Jenkins jobs
 * outside a multi-branch pipeline have no branch ref, GitLab
 * non-MR jobs have no `prId`, GHA scheduled runs have no `prId`,
 * etc. The CLI uses `null` to mean "didn't detect", never "" so
 * downstream consumers can keep the difference between absent and
 * empty.
 */
export const CiEnvSchema = z.object({
  provider: CiConcreteProviderSchema,
  prId: z.number().int().min(1).nullable(),
  commitSha: z.string().min(7).nullable(),
  branch: z.string().min(1).nullable(),
  buildId: z.string().min(1).nullable(),
  projectSlug: z.string().min(1).nullable(),
  /**
   * GitLab-only — `CI_API_V4_URL` from the GitLab Runner. Kept on
   * the env shape (rather than re-resolved at post time) so the
   * allow-list check happens once at the boundary.
   */
  gitlabApiBaseUrl: z.string().url().nullable().optional(),
});
export type CiEnv = z.infer<typeof CiEnvSchema>;

/**
 * Detection order — outermost wins. Documented as a constant so a
 * downstream renderer that wants to label a run "GHA inside
 * Jenkins" can reason about the same priority list.
 */
export const CI_DETECT_ORDER: readonly CiConcreteProvider[] = ['github', 'gitlab', 'jenkins'] as const;

/**
 * Pure detector. Pass a plain env bag (e.g. `process.env` from the
 * caller, or a stub `{ GITHUB_ACTIONS: 'true' }` from a test).
 * Returns the fully-normalised `CiEnv`.
 *
 *   detectCiProvider({ GITHUB_ACTIONS: 'true', GITHUB_RUN_ID: '7', GITHUB_SHA: 'abcdef1', GITHUB_REF_NAME: 'main', GITHUB_REPOSITORY: 'owner/repo' })
 *     → { provider: 'github', prId: null, commitSha: 'abcdef1', branch: 'main', buildId: '7', projectSlug: 'owner/repo' }
 *
 *   detectCiProvider({})
 *     → { provider: 'unknown', prId: null, commitSha: null, branch: null, buildId: null, projectSlug: null }
 *
 *   detectCiProvider({ JENKINS_HOME: '/var/j', BUILD_NUMBER: '42', GIT_BRANCH: 'origin/main', GIT_COMMIT: 'abcdef1', JOB_NAME: 'team/app/main' })
 *     → { provider: 'jenkins', prId: null, commitSha: 'abcdef1', branch: 'main', buildId: '42', projectSlug: 'team/app/main' }
 */
export function detectCiProvider(env: Readonly<Record<string, string | undefined>>): CiEnv {
  // GitHub Actions
  if (env.GITHUB_ACTIONS === 'true') {
    const ref = env.GITHUB_REF_NAME ?? null;
    const prRaw =
      env.GITHUB_EVENT_NAME === 'pull_request' && env.GITHUB_REF ? parsePrFromGithubRef(env.GITHUB_REF) : null;
    return CiEnvSchema.parse({
      provider: 'github',
      prId: prRaw,
      commitSha: env.GITHUB_SHA ?? null,
      branch: ref,
      buildId: env.GITHUB_RUN_ID ?? null,
      projectSlug: env.GITHUB_REPOSITORY ?? null,
    });
  }

  // GitLab CI
  if (env.GITLAB_CI === 'true') {
    return CiEnvSchema.parse({
      provider: 'gitlab',
      prId: env.CI_MERGE_REQUEST_IID ? Number.parseInt(env.CI_MERGE_REQUEST_IID, 10) : null,
      commitSha: env.CI_COMMIT_SHA ?? null,
      branch: env.CI_MERGE_REQUEST_SOURCE_BRANCH_NAME ?? env.CI_COMMIT_REF_NAME ?? null,
      buildId: env.CI_JOB_ID ?? null,
      projectSlug: env.CI_PROJECT_PATH ?? null,
      gitlabApiBaseUrl: env.CI_API_V4_URL ?? null,
    });
  }

  // Jenkins — needs both signals because BUILD_NUMBER alone is
  // common in non-Jenkins envs (Codeship, custom shell scripts).
  if (env.JENKINS_HOME && env.BUILD_NUMBER) {
    return CiEnvSchema.parse({
      provider: 'jenkins',
      // Jenkins has no first-class MR concept on the env. Multi-
      // branch pipelines expose `CHANGE_ID` for PR builds.
      prId: env.CHANGE_ID ? Number.parseInt(env.CHANGE_ID, 10) : null,
      commitSha: env.GIT_COMMIT ?? null,
      branch: normaliseGitRef(env.GIT_BRANCH ?? null),
      buildId: env.BUILD_NUMBER ?? null,
      projectSlug: env.JOB_NAME ?? null,
    });
  }

  return CiEnvSchema.parse({
    provider: 'unknown',
    prId: null,
    commitSha: null,
    branch: null,
    buildId: null,
    projectSlug: null,
  });
}

/**
 * GHA's `GITHUB_REF` on `pull_request` events is shaped like
 * `refs/pull/123/merge` — extract the PR id, return null on
 * anything else.
 */
function parsePrFromGithubRef(ref: string): number | null {
  const m = /^refs\/pull\/(\d+)\/(?:merge|head)$/.exec(ref);
  return m ? Number.parseInt(m[1], 10) : null;
}

/**
 * Jenkins emits `GIT_BRANCH` as `origin/main`. Normalise to
 * `main` so consumers don't have to special-case the remote name.
 * Already-normalised input passes through unchanged.
 */
function normaliseGitRef(ref: string | null): string | null {
  if (!ref) return null;
  return ref.replace(/^(?:origin|refs\/heads)\//, '');
}

/**
 * Allow-list for the GitLab REST API base URL. Refuses anything
 * that isn't an HTTPS host with `/api/v4` as the path. Guards
 * against a hostile `CI_API_V4_URL` injection pivoting the token
 * to an attacker server.
 */
export function isAllowedGitlabApiBaseUrl(url: string | null | undefined): url is string {
  if (!url) return false;
  return /^https:\/\/[^/]+\/api\/v4$/.test(url);
}

/**
 * Map a Rynora flow priority onto a GitLab Code Quality severity.
 * Default for a row without an explicit priority is `'major'` —
 * Code Quality's typical "default failing rule" level.
 */
export const FLOW_PRIORITY_TO_CODE_QUALITY_SEVERITY = {
  low: 'info',
  medium: 'minor',
  high: 'major',
  critical: 'critical',
} as const;
export type FlowPriority = keyof typeof FLOW_PRIORITY_TO_CODE_QUALITY_SEVERITY;
export type CodeQualitySeverity = 'info' | 'minor' | 'major' | 'critical' | 'blocker';

/**
 * GitLab Code Quality finding — a flat JSON array, one entry per
 * failed flow. The MR sidebar widget keys on `fingerprint` for
 * "unchanged from previous run" semantics.
 */
export const GitlabCodeQualityFindingSchema = z.object({
  description: z.string().min(1),
  severity: z.enum(['info', 'minor', 'major', 'critical', 'blocker']),
  fingerprint: z.string().length(64),
  location: z.object({
    path: z.string().min(1),
    lines: z.object({ begin: z.number().int().min(1) }),
  }),
});
export type GitlabCodeQualityFinding = z.infer<typeof GitlabCodeQualityFindingSchema>;

export const GitlabCodeQualityReportSchema = z.array(GitlabCodeQualityFindingSchema);
export type GitlabCodeQualityReport = z.infer<typeof GitlabCodeQualityReportSchema>;

/**
 * Sparse summary shape the emitters/posters work against. The CLI
 * builds this from the existing `RunResult` array before handing
 * off to a provider-specific module so each provider doesn't
 * re-walk the same data.
 */
export const CiRunSummarySchema = z.object({
  total: z.number().int().min(0),
  passed: z.number().int().min(0),
  failed: z.number().int().min(0),
  skipped: z.number().int().min(0),
  durationMs: z.number().int().min(0),
  /**
   * Suite-run UUID — same shape `--suite-run-id` accepts on the
   * runner side. When set, status posters build deep-links into
   * the Rynora app's run viewer.
   */
  suiteRunId: z.string().min(1).nullable(),
  /**
   * Up to N failing flows (capped by the caller) for status-post
   * bodies. Always-present field; emit empty array when nothing
   * failed.
   */
  topFailures: z
    .array(
      z.object({
        flowId: z.string().min(1),
        flowName: z.string().min(1),
        firstFailingStep: z.string().nullable(),
        priority: z.enum(['low', 'medium', 'high', 'critical']).nullable(),
      }),
    )
    .max(50),
  /**
   * Requirement #12 — optional Quality Gate fragment from #11.
   * When set, `dispatchCiEmit` writes `rynora-quality-gate.json`
   * and status posters include the gate verdict. Absent for
   * legacy callers that only care about pass/fail counts.
   */
  qualityGate: z
    .object({
      verdict: z.enum(['pass', 'warn', 'fail']),
      exitCode: z.union([z.literal(0), z.literal(1), z.literal(2)]),
      scope: z.enum(['run', 'flow', 'scenario', 'execution', 'suite']),
      sampleSize: z.number().int().nonnegative(),
      passRate: z.number().min(0).max(100).nullable(),
      failureCount: z.number().int().nonnegative(),
      healingRate: z.number().min(0).max(100).nullable(),
      unstableRate: z.number().min(0).max(100).nullable(),
      reasons: z
        .array(
          z.object({
            code: z.string(),
            severity: z.enum(['pass', 'warn', 'fail']),
            message: z.string(),
            actual: z.number().nullable().optional(),
            threshold: z.number().nullable().optional(),
          }),
        )
        .max(20),
      enginePath: z.literal('step-processor').optional(),
      evaluatedAt: z.string(),
    })
    .optional(),
});
export type CiRunSummary = z.infer<typeof CiRunSummarySchema>;

/**
 * Build the deterministic fingerprint a GitLab Code Quality entry
 * is keyed by. `flowId + firstFailingStepId` is stable across runs
 * — the MR widget then folds successive runs of the same failure
 * into one row.
 *
 * Returns a 64-char lowercase hex string suitable for the
 * `fingerprint` slot.
 */
export async function computeCodeQualityFingerprint(
  flowId: string,
  firstFailingStepId: string | null,
): Promise<string> {
  const payload = `${flowId}\u0000${firstFailingStepId ?? ''}`;
  // Node 18+ ships WebCrypto via `globalThis.crypto`. Fall back to
  // node:crypto for older runtimes the CLI may be invoked under.
  if (typeof globalThis.crypto?.subtle?.digest === 'function') {
    const buf = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(payload));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  // Lazy-require so the contracts package stays browser-clean.

  const nodeCrypto = require('crypto') as typeof import('crypto');
  return nodeCrypto.createHash('sha256').update(payload).digest('hex');
}

/**
 * Truncate a string to a safe length for inclusion in a status-post
 * body (MR comment / build description). 4 KB is plenty for our
 * single-line summary + first 5 failures and stays well under
 * GitLab's 1 MB comment cap and Jenkins' 64 KB build-description
 * cap. Adds an ellipsis when truncated.
 *
 * Walks code-points (NOT bytes) so multi-byte runes aren't split
 * mid-encoding; the trailing ellipsis is itself 3 UTF-8 bytes so we
 * reserve those before the walk.
 */
export function truncateForStatusPost(input: string, maxBytes = 4096): string {
  if (Buffer.byteLength(input, 'utf8') <= maxBytes) return input;
  const ELLIPSIS = '\u2026';
  const ellipsisBytes = Buffer.byteLength(ELLIPSIS, 'utf8');
  const budget = Math.max(0, maxBytes - ellipsisBytes);
  let used = 0;
  let out = '';
  for (const cp of input) {
    const w = Buffer.byteLength(cp, 'utf8');
    if (used + w > budget) break;
    out += cp;
    used += w;
  }
  return out + ELLIPSIS;
}
